import React from "react";
import { connect } from "react-redux";

import { SimManager } from "@learningblocks/simulation-engine";

import {
  ProtocolEquipmentModal,
  type TooltipProps,
} from "../ProtocolEquipmentModal";

import OvenEmpty from "./images/drying_oven_empty.svg";
import OvenWeighBoat from "./images/drying_oven_weigh_boat.svg";
import OvenPlant7Day from "./images/drying_oven_7_day_plant.svg";
import OvenPlant7DayDried from "./images/drying_oven_7_day_dried.svg";
import OvenPlant14DayDried from "./images/drying_oven_14_day_dried.svg";
import PowerButton from "./images/power_button.svg";
import PowerButtonHover from "./images/power_button_hover.svg";
import ArrowUp from "./images/arrow_up.svg";
import ArrowUpHover from "./images/arrow_up_hover.svg";
import ArrowDown from "./images/arrow_down.svg";
import ArrowDownHover from "./images/arrow_down_hover.svg";

import "./DryingOvenModal.scss";

const OVEN_IMAGES: Record<string, string> = {
  closed: OvenEmpty,
  open: OvenEmpty,
  closed__weigh_boat: OvenWeighBoat,
  open__weigh_boat: OvenWeighBoat,
  closed__weigh_boat__plant_day_7: OvenPlant7Day,
  open__weigh_boat__plant_day_7: OvenPlant7Day,
  closed__weigh_boat__plant_day_14: OvenPlant7Day,
  open__weigh_boat__plant_day_14: OvenPlant7Day,
  closed__weigh_boat__plant_day_7_oven_dried: OvenPlant7DayDried,
  open__weigh_boat__plant_day_7_oven_dried: OvenPlant7DayDried,
  closed__weigh_boat__plant_day_14_oven_dried: OvenPlant14DayDried,
  open__weigh_boat__plant_day_14_oven_dried: OvenPlant14DayDried,
};

interface RootState {
  simulation: {
    dryingOvenOn: boolean;
    dryingOvenTemperature: number;
  };
}

export interface DryingOvenModalOwnProps {
  show: boolean;
  close: () => void;
  tooltip?: TooltipProps;
  getState: () => RootState;
}

export interface DryingOvenModalProps extends DryingOvenModalOwnProps {
  isOn: boolean;
  temperature: number;
}

interface DryingOvenModalState {
  powerHovered: boolean;
  upHovered: boolean;
  downHovered: boolean;
}

class DryingOvenModalComponent extends React.Component<
  DryingOvenModalProps,
  DryingOvenModalState
> {
  static defaultProps = {
    tooltip: {
      visible: false,
      pos: undefined,
      title: "",
      message: "",
    },
  };

  state: DryingOvenModalState = {
    powerHovered: false,
    upHovered: false,
    downHovered: false,
  };

  handlePowerToggle = () => {
    SimManager.dispatchAction("TOGGLE_DRYING_OVEN", "drying_oven");
  };

  handleTemperatureUp = () => {
    SimManager.dispatchAction("INCREASE_OVEN_TEMPERATURE", "drying_oven");
  };

  handleTemperatureDown = () => {
    SimManager.dispatchAction("DECREASE_OVEN_TEMPERATURE", "drying_oven");
  };

  computeOvenImage = (): string => {
    const ovenState = SimManager.getObjectState("drying_oven") || "closed";
    return OVEN_IMAGES[ovenState] || OvenEmpty;
  };

  renderRightColumn = () => {
    const { temperature, isOn } = this.props;
    const ovenImage = this.computeOvenImage();

    const { powerHovered, upHovered, downHovered } = this.state;

    return (
      <div className="oven">
        <img
          src={ovenImage || OvenEmpty}
          alt="Drying Oven"
          className="oven-image"
        />
        <div className="temperature-display">
          <span className={`temperature-value ${isOn ? "on" : "off"}`}>
            {isOn ? `${temperature}°C` : "OFF"}
          </span>
        </div>

        <div className="oven-control-panel">
          <button
            className={`control-button power-button ${isOn ? "on" : "off"}`}
            onClick={this.handlePowerToggle}
            onMouseEnter={() => this.setState({ powerHovered: true })}
            onMouseLeave={() => this.setState({ powerHovered: false })}
            aria-label={isOn ? "Turn off oven" : "Turn on oven"}
          >
            <img
              src={powerHovered ? PowerButtonHover : PowerButton}
              alt="Power"
            />
          </button>
          <div className="temperature-controls">
            <button
              className="control-button up-button"
              onClick={this.handleTemperatureUp}
              onMouseEnter={() => this.setState({ upHovered: true })}
              onMouseLeave={() => this.setState({ upHovered: false })}
              disabled={!isOn}
              aria-label="Increase temperature"
            >
              <img src={upHovered ? ArrowUpHover : ArrowUp} alt="Increase" />
            </button>
            <button
              className="control-button down-button"
              onClick={this.handleTemperatureDown}
              onMouseEnter={() => this.setState({ downHovered: true })}
              onMouseLeave={() => this.setState({ downHovered: false })}
              disabled={!isOn}
              aria-label="Decrease temperature"
            >
              <img
                src={downHovered ? ArrowDownHover : ArrowDown}
                alt="Decrease"
              />
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { show, close, tooltip } = this.props;

    return (
      <ProtocolEquipmentModal
        show={show}
        close={close}
        equipmentImage={this.computeOvenImage()}
        tooltip={tooltip!}
        className="drying-oven-modal"
        singleColumnView={true}
        renderRightColumn={this.renderRightColumn}
      />
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  isOn: state.simulation.dryingOvenOn,
  temperature: state.simulation.dryingOvenTemperature,
});

const mapDispatchToProps = (
  _dispatch: unknown,
  ownProps: DryingOvenModalOwnProps
) => ({
  getState: ownProps.getState,
});

export const DryingOvenModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(DryingOvenModalComponent);

export default DryingOvenModal;
